import { FC, memo, useState, useEffect, useCallback, useMemo } from 'react';
import {
	Box,
	Image,
	Skeleton,
	Text,
	IconButton,
	HStack,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	useDisclosure,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import ProductFormModal from 'src/components/commerce/products/ProductFormModal';
import { useCatalogsContext } from 'src/contexts/catalogs/CatalogsContext';
import useCatalogKinds from 'src/hooks/useCatalogKinds';
import useCopilotChatContext from 'src/fusion-copilot/hooks/useCopilotChatContext';
import { useChatData } from 'src/fusion-copilot/contexts/ChatDataContext';
import { deleteAccountConfigOptions } from 'src/services/account';
import { toastError } from 'src/services/toast';
import { ICatalogForm } from 'src/lib/schemas';

interface CatalogPreviewProps {
	catalogId: string;
}

const loadedImagesCache = new Set<string>();

const CatalogPreview: FC<CatalogPreviewProps> = memo(({ catalogId }) => {
	const { getCatalogById, isLoading, refetch, updateCatalog } =
		useCatalogsContext();
	const { catalogKinds } = useCatalogKinds();
	const { removeCatalog } = useCopilotChatContext();
	const { products } = useChatData();
	const apiCatalog = getCatalogById(catalogId);
	const [isDeleted, setIsDeleted] = useState(false);

	// If not found in API catalogs, look in chat products (scraped catalogs)
	const chatProduct = useMemo(() => {
		if (apiCatalog) return null;

		// Try to decode base64 catalogId (backend may send name encoded as base64)
		let decodedName = '';
		try {
			decodedName = atob(catalogId);
		} catch {
			// Not base64, use as-is
		}

		console.log('[CatalogPreview] Looking for catalog:', {
			catalogId,
			decodedName,
			productsCount: products?.length || 0,
			productNames: products?.map((p: any) => p.name).slice(0, 5),
		});

		const found = products.find(
			(p) =>
				p.catalogId === catalogId ||
				p.id === catalogId ||
				p.name === catalogId ||
				(decodedName && p.name === decodedName)
		);

		console.log('[CatalogPreview] Found:', found?.name || 'null');
		return found;
	}, [apiCatalog, products, catalogId]);

	// Unified catalog object - prefer chat product if found (more reliable), fallback to API catalog
	const catalog = useMemo(() => {
		// If we found a matching catalog in chat context, prefer it
		// This handles cases where the catalogId might be stale/wrong but the name matches
		if (chatProduct) {
			console.log('[CatalogPreview] Using chatProduct:', chatProduct.name);
			return {
				id: chatProduct.id || chatProduct.catalogId || '',
				catalogId: chatProduct.catalogId || chatProduct.id || '',
				name: chatProduct.name,
				description: chatProduct.description,
				price: chatProduct.price,
				image: chatProduct.image,
				kind: chatProduct.kind || chatProduct.type || 'service',
				// Include details for ProductFormModal compatibility
				details: {
					description: chatProduct.description || '',
					image: chatProduct.image || '',
				},
			};
		}
		// Fallback to API catalog if no chat product found
		if (apiCatalog) {
			console.log('[CatalogPreview] Using apiCatalog:', apiCatalog.name);
			return apiCatalog;
		}
		console.log('[CatalogPreview] No catalog found for:', catalogId);
		return null;
	}, [apiCatalog, chatProduct, catalogId]);

	const imageToShow = catalog?.image || '/images/service-placeholder.png';
	const [isLoaded, setIsLoaded] = useState(
		imageToShow ? loadedImagesCache.has(imageToShow) : false,
	);
	const {
		isOpen: isEditModalOpen,
		onOpen: onEditModalOpen,
		onClose: onEditModalClose,
	} = useDisclosure();
	const {
		isOpen: isDeleteModalOpen,
		onOpen: onDeleteModalOpen,
		onClose: onDeleteModalClose,
	} = useDisclosure();

	useEffect(() => {
		if (isLoaded && imageToShow) {
			loadedImagesCache.add(imageToShow);
		}
	}, [isLoaded, imageToShow]);

	const handleEdit = useCallback(() => {
		onEditModalOpen();
	}, [onEditModalOpen]);

	const handleDelete = useCallback(() => {
		onDeleteModalOpen();
	}, [onDeleteModalOpen]);

	const handleConfirmDelete = useCallback(async () => {
		try {
			if (catalog) {
				await deleteAccountConfigOptions('catalogs', catalog.id);
				setIsDeleted(true);

				// Remove from chat context
				if (removeCatalog && catalog.catalogId) {
					removeCatalog(catalog.catalogId);
				}

				// Dispatch custom event to notify other components
				window.dispatchEvent(
					new CustomEvent('catalog-deleted', {
						detail: { catalogId: catalog.catalogId },
					}),
				);

				await refetch();
			}
		} catch (error) {
			toastError(error);
		}
		onDeleteModalClose();
	}, [catalog, refetch, onDeleteModalClose, removeCatalog]);

	const handleCreateUpdateProduct = useCallback(
		async (payload: ICatalogForm) => {
			try {
				if (catalog) {
					await updateCatalog(payload);
					onEditModalClose();
				}
			} catch (error) {
				toastError(error);
			}
		},
		[catalog, updateCatalog, onEditModalClose],
	);

	if (isLoading) {
		return (
			<Box
				borderRadius="xl"
				overflow="hidden"
				p={4}
				mb={4}
				display="flex"
				flexDirection="row"
				alignItems="center"
				gap={4}
				bg="white"
			>
				<Skeleton h="100px" w="100px" borderRadius="lg" />
				<Skeleton h="20px" w="150px" />
			</Box>
		);
	}

	if (!catalog || isDeleted) {
		return null;
	}

	return (
		<>
			<Box
				borderRadius="xl"
				overflow="hidden"
				p={4}
				display="flex"
				flexDirection="row"
				alignItems="center"
				gap={4}
				bg="gray.50"
				border="1px solid"
				borderColor="gray.200"
				w="100%"
			>
				<Box
					position="relative"
					w="100px"
					h="100px"
					borderRadius="lg"
					overflow="hidden"
					boxShadow="sm"
					flexShrink={0}
					bg="gray.100"
				>
					{!isLoaded && <Skeleton h="100px" w="100px" borderRadius="lg" />}
					<Image
						src={imageToShow}
						alt={catalog.name}
						onError={(e) => {
							if (imageToShow !== '/images/service-placeholder.png') {
								console.error('Error loading image:', imageToShow);
							}
							setIsLoaded(true);
							e.currentTarget.style.display = 'none';
						}}
						onLoad={() => setIsLoaded(true)}
						display={isLoaded ? 'block' : 'none'}
						objectFit="cover"
						w="100px"
						h="100px"
					/>
				</Box>
				<Box
					flex="1"
					display="flex"
					flexDirection="column"
					justifyContent="center"
				>
					<Text
						fontSize="lg"
						fontWeight="semibold"
						color="gray.800"
						noOfLines={3}
						mb={2}
					>
						{catalog.name}
					</Text>
					<HStack spacing={1} justifyContent="flex-end">
						<IconButton
							aria-label="Edit catalog"
							icon={<EditIcon />}
							size="sm"
							variant="ghost"
							colorScheme="blue"
							onClick={handleEdit}
							_hover={{
								bg: 'blue.50',
							}}
						/>
						<IconButton
							aria-label="Remove catalog"
							icon={<DeleteIcon />}
							size="sm"
							variant="ghost"
							colorScheme="red"
							onClick={handleDelete}
							_hover={{
								bg: 'red.50',
							}}
						/>
					</HStack>
				</Box>
			</Box>

			<ProductFormModal
				isOpen={isEditModalOpen}
				onClose={onEditModalClose}
				onSubmit={handleCreateUpdateProduct}
				title="Edit Catalog"
				catalogKinds={catalogKinds}
				initialValues={catalog}
			/>

			<Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
				<ModalOverlay zIndex={1800} />
				<ModalContent
					margin="auto"
					containerProps={{
						zIndex: 1900,
					}}
				>
					<ModalHeader>Confirm Deletion</ModalHeader>
					<ModalBody>Are you sure you want to delete this catalog?</ModalBody>
					<ModalFooter>
						<Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
							Cancel
						</Button>
						<Button colorScheme="secondary" onClick={handleConfirmDelete}>
							Delete
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
});

CatalogPreview.displayName = 'CatalogPreview';

export default CatalogPreview;
